import React from 'react';
import { Box, Container, Typography, Stack, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import TuneIcon from '@mui/icons-material/Tune';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SpeedIcon from '@mui/icons-material/Speed';

const reasons = [
  {
    icon: <PersonIcon sx={{ fontSize: 28, color: '#38BDF8' }} />,
    title: 'Atención directa con el desarrollador',
    desc: 'No hay intermediarios. Hablas directamente con quien construye tu solución, desde el primer día hasta el último.',
  },
  {
    icon: <TuneIcon sx={{ fontSize: 28, color: '#38BDF8' }} />,
    title: 'Sin plantillas genéricas',
    desc: 'Cada solución se diseña desde cero para tu negocio. No adaptamos tu operación a un software — adaptamos el software a tu operación.',
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 28, color: '#38BDF8' }} />,
    title: 'Compromiso real',
    desc: 'No desaparecemos después de entregar. Si algo necesita ajuste o mejora, estamos ahí. Tu éxito es nuestra referencia.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 28, color: '#38BDF8' }} />,
    title: 'Foco en resultados',
    desc: 'No construimos código por construir. Construimos herramientas que ahorran tiempo, reducen errores y hacen crecer tu negocio.',
  },
];

const WhyEffsos = () => {
  return (
    <Box
      id="about"
      sx={{
        background: 'linear-gradient(135deg, #083a94 0%, #0a3fa8 100%)',
        py: { xs: 8, md: 12 },
        color: 'white',
      }}
    >
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 6, lg: 10 }} alignItems="flex-start">

          {/* Lado izquierdo - texto principal */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ flex: '0 0 auto', maxWidth: 420 }}
          >
            <Typography
              variant="overline"
              display="block"
              sx={{ color: '#38BDF8', fontWeight: 700, letterSpacing: 3, mb: 1 }}
            >
              POR QUÉ EFFSOS
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, mb: 3, lineHeight: 1.15, fontSize: { xs: '2rem', md: '2.6rem' } }}
            >
              Boutique tech para tu negocio
            </Typography>
            <Typography
              sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontSize: '1.05rem', mb: 3 }}
            >
              Effsos nació con una idea clara: que una pequeña empresa también
              merece tecnología hecha a su medida. No el software que todos usan —
              el software que tú necesitas.
            </Typography>
            <Typography
              sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontSize: '1.05rem' }}
            >
              Somos un equipo pequeño y comprometido. Eso significa más atención,
              más calidad y más responsabilidad en cada proyecto que tomamos.
            </Typography>

            {/* Separador con dato */}
            <Box
              sx={{
                mt: 5,
                pt: 4,
                borderTop: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <Typography
                sx={{ fontSize: '3rem', fontWeight: 900, color: '#38BDF8', lineHeight: 1 }}
              >
                10+
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                años de experiencia en tecnología y sistemas
              </Typography>
            </Box>
          </motion.div>

          {/* Lado derecho - tarjetas */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={2.5}>
              {reasons.map((r, i) => (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 3,
                      backdropFilter: 'blur(4px)',
                      transition: 'background 0.3s',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.12)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2.5} alignItems="flex-start">
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: 'rgba(56,189,248,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {r.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                          {r.title}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                          {r.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </motion.div>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default WhyEffsos;
